/**
 * This file contains the Initialiser class, which is responsible for initializing various components and configurations
 * required for the application to run. It includes methods for setting up routing, services, environment information,
 * and other core functionalities. The Initialiser class also provides methods for launching views, portals, and mashups,
 * as well as importing necessary assets and configurations.
 *
 * Key functionalities include:
 * - Initializing routing, action target models, and services.
 * - Fetching bootstrap configuration from the server.
 * - Initializing core containers and configurations.
 * - Importing React root and design system component maps.
 * - Building semantic URLs.
 * - Launching views, portals, and mashups.
 * - Registering for debug information and toggling tracer headers.
 * - Loading environment information.
 *
 * This class is essential for setting up and managing the application's core infrastructure and ensuring that all necessary
 * components and configurations are properly initialized and ready for use.
 */

import { enableAppRouting, init } from '../router/app-router';
import configureStore from '../store';
import EnvironmentInfo from '../environment-info';
import FieldDefaultUtils from '../fields/field-default';
import TargetMap from '../actions/targetmap';
import Services from '../services';
import AssetLoader from '../utils/asset-loader';
import { getLocaleUtils } from '../locale/locale-utils';
import RemoteCase from '../case/remote-case';
import MessagingServiceManager from '../messagingservice/manager';
import NavigationUtils from '../utils/navigation-utils';
import CoexistenceManager from '../coexistence';
import HeaderProcessor from '../service_broker/header-processor';
import { createC11nEnv as createC11nEnvInstance } from '../interpreter/c11n-env';
import BootstrapUtils from '../bootstrap/bootstrap-utils';
import RuntimeParamsAPI from '../runtime-params';
import PubSubUtils from '../utils/pubsub-utils';
import type { RoutingInfo } from '../router/types';
import type { ServicesType } from '../services/types';
import type { CookieComplianceMethod } from '../environment-info/types';
import Constants from '../constants';

import Configuration, { type ConfigurationType } from './configurations';
import loadLocalizationAssets from './init-localization';

const viewConfig = '@P .viewConfig';
const routingInfoAnnotation = '@ROUTING_INFO';

let containerCount = 0;

/**
 * The Initialiser class is responsible for initializing various components and configurations
 * required for the application to run. It includes methods for setting up routing, services,
 * environment information, and other core functionalities.
 */
class Initialiser {
  /**
   * Initializes the application with the provided configuration.
   * @function
   * @param configObj - The configuration object containing various settings and parameters.
   * @example
   * const config = { ... };
   * initialiser.init(config);
   * @returns Promise<void>
   */
  async init(configObj: ConfigurationType): Promise<void> {
    const config = new Configuration(configObj);
    const envInfo = { ...config.environmentInfo, renderingMode: config.renderingMode };

    enableAppRouting(config.enableRouting);

    configureStore({});

    EnvironmentInfo.setTheme(config.theme);
    EnvironmentInfo.setCookieComplianceMethod(config.cookieComplianceMethod);
    EnvironmentInfo.setIsExtraCallRequiredForDefaultHome(config.isExtraCallRequiredForDefaultHome);

    /* Set default field configs */
    FieldDefaultUtils.updateFieldDefaults(config.fieldDefaults);
    FieldDefaultUtils.setFieldDefault('Percentage', 'storePercentageAsDecimal', config.storePercentageAsDecimal);

    /* Set default headers */
    HeaderProcessor.setDefaultHeaders(config.additionalHeaders);

    /* Initialize server config */
    this.#initRouting(config.routingInfo, config.actionModel, {
      server: config.restServerConfig,
      reAuthUrl: config.serviceConfig.reAuthUrl
    });
    AssetLoader.setAppAlias(config.serviceConfig.appAliasWithoutServlet || config.serviceConfig.appAlias);
    Services.setGoogleMapsAPIKey(config.serviceConfig.googleMapKey);
    AssetLoader.initServer(
      config.serviceConfig.staticContentServer,
      config.serviceConfig.appStaticContentServer,
      config.tokens.C11NB2S
    );

    EnvironmentInfo.setEnvironmentInfo(envInfo);
    EnvironmentInfo.setLocale(config.locale || navigator.language || 'en-US');
    getLocaleUtils().setTimezone(config.timezone);
    RemoteCase.setRemoteCaseMapping(config.remoteCaseMapping);
    MessagingServiceManager.setConnectionConfig({ messagingService: config.serviceConfig.messagingService });
    NavigationUtils.init();

    if (config.initBroadcast) {
      CoexistenceManager.getBroadcastUtils().init();
    }

    await loadLocalizationAssets();
  }

  /**
   * Fetches the bootstrap configuration from the server.
   * @function
   * @param restServerUrl - The URL of the REST server to fetch the configuration from.
   * @param tokenInfo - An object containing the token type and access token for authorization.
   * @returns A promise that resolves with the bootstrap configuration data.
   * @example
   * const restServerUrl = 'https://example.com';
   * const tokenInfo = { token_type: 'Bearer', access_token: 'abc123' };
   * initialiser.getBootstrapConfig(restServerUrl, tokenInfo).then(config => console.log(config));
   */
  getBootstrapConfig = async (
    restServerUrl: string,
    tokenInfo: { token_type: string; access_token: string }
  ): Promise<any> => {
    const authorizationHeader = `${tokenInfo.token_type} ${tokenInfo.access_token}`;

    return fetch(`${restServerUrl}/api/application/v2/data_views/D_pxBootstrapConfig`, {
      method: 'GET',
      headers: new Headers({
        Authorization: authorizationHeader
      })
    }).then((response) => response.json());
  };

  /**
   * Initializes core containers with the specified options.
   * @function
   * @param options - An object containing options for initializing core containers.
   * @returns An object containing the getPConnect function.
   * @example
   * const options = { containerType: 'single' };
   * const coreContainers = initialiser.initCoreContainers(options);
   * console.log(coreContainers.props.getPConnect());
   */
  initCoreContainers = (options: { containerType?: string } = {}): { props: { getPConnect: () => any } } => {
    const { containerType = 'single' } = options;
    const rootMashUp = {
      type: 'RootContainer',
      config: {
        renderingMode: 'noPortal'
      },
      children: [
        {
          type: 'ViewContainer',
          config: {
            routingInfo: '@ROUTING_INFO',
            name: 'primary'
          }
        }
      ]
    };

    const rootPConnect = createC11nEnvInstance({
      meta: rootMashUp,
      options: {
        context: 'root',
        pageReference: '',
        target: 'root'
      }
    });
    rootPConnect.getPConnect().getContainerManager().initializeContainers({
      type: containerType,
      name: 'primary',
      context: 'app'
    });

    return {
      props: {
        getPConnect: rootPConnect.getPConnect
      }
    };
  };

  /**
   * Imports the React root component.
   * @function
   * @returns A promise that resolves when the React root component is imported.
   * @example
   * initialiser.importReactRoot().then(() => console.log('React root imported'));
   */
  importReactRoot = async (): Promise<any> => {
    const promises: Promise<any>[] = [];
    BootstrapUtils.getComponentsRegistry()
      .getComponent('ReactRoot')
      ?.modules.forEach((module: () => Promise<any>) => {
        promises.push(module());
      });
    return Promise.allSettled(promises);
  };

  /**
   * Imports the design system component map.
   * @function
   * @param appStaticContentServer - The URL of the static content server.
   * @param environmentId - The ID of the environment.
   * @param alternateDesignSystemName - The name of the alternate design system.
   * @returns A promise that resolves when the design system component map is imported.
   * @example
   * const serverUrl = 'https://static.example.com';
   * const envId = 'env123';
   * const designSystemName = 'alternateDS';
   * initialiser.importDesignSystemComponentMap(serverUrl, envId, designSystemName).then(() => console.log('Design system imported'));
   */
  importDesignSystemComponentMap = async (
    appStaticContentServer: string,
    environmentId: string,
    alternateDesignSystemName: string
  ): Promise<void> => {
    function loadDesignSystemJS(FILE_URL: string, async = 'true') {
      return new Promise((resolve: any, reject) => {
        const scriptEle = document.createElement('script');
        scriptEle.setAttribute('type', 'text/javascript');
        scriptEle.setAttribute('src', FILE_URL);
        scriptEle.setAttribute('async', async);
        scriptEle.onload = resolve();
        scriptEle.onerror = () => reject(new Error('Failed to load resource from Design System'));
        document.body.appendChild(scriptEle);
      });
    }

    try {
      await loadDesignSystemJS(
        `${appStaticContentServer}component/project/${environmentId}/designsystems/${alternateDesignSystemName}/componentsmap.js?v=${Date.now()}`
      );
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * Builds a semantic URL based on the provided parameters.
   * @function
   * @param constellationPathInfo - The path information for the constellation.
   * @param queryParams - The query parameters to include in the URL.
   * @param noPortal - A flag indicating whether the portal should be excluded.
   * @param restServer - The URL of the REST server.
   * @param viewName - The name of the view to include in the URL.
   * @returns The constructed semantic URL as a string.
   * @example
   * const pathInfo = '/case123';
   * const queryParams = new URLSearchParams({ key: 'value' });
   * const noPortal = 'true';
   * const restServer = 'https://api.example.com';
   * const viewName = 'view123';
   * const url = initialiser.buildSemanticUrl(pathInfo, queryParams, noPortal, restServer, viewName);
   * console.log(url);
   */
  buildSemanticUrl = (
    constellationPathInfo: string,
    queryParams: URLSearchParams,
    noPortal: string,
    restServer: string,
    viewName: string
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ): string => {
    const urlSearchParams = new URLSearchParams(queryParams);

    let isNoPortalParam = false;
    if (noPortal === 'true') {
      isNoPortalParam = true;
    }

    let caseOrPageName: string | null = null;
    let caseWork: string | null = null;
    let assignmentKey: string | null = null;
    if (constellationPathInfo) {
      if (constellationPathInfo.startsWith('/')) {
        constellationPathInfo = constellationPathInfo.substring(1);
      }
      const pathTokens = constellationPathInfo.split('/');
      caseOrPageName = pathTokens[0];
      if (pathTokens.length > 1 && pathTokens[1]) {
        caseWork = pathTokens[1];
      }
      if (pathTokens.length > 1 && pathTokens[2] === 'assignments' && pathTokens[3]) {
        assignmentKey = pathTokens[3];
      }
    } else {
      isNoPortalParam = false;
    }

    let constellationUrl = restServer;
    if (caseOrPageName) {
      constellationUrl = `${constellationUrl}/${caseOrPageName}`;
    }
    if (caseWork) {
      constellationUrl = `${constellationUrl}/${caseWork}`;
    }
    if (assignmentKey) {
      constellationUrl = `${constellationUrl}/assignments/${assignmentKey}`;
    }

    if (isNoPortalParam) {
      if (!urlSearchParams.has('noPortal')) {
        urlSearchParams.append('noPortal', 'true');
      }
      if (caseWork && viewName) {
        urlSearchParams.append('view', viewName);
      }
    }
    const searchParamsString = urlSearchParams.toString();
    constellationUrl += searchParamsString ? `?${searchParamsString}` : '';

    return constellationUrl;
  };

  /**
   * Initializes the core configuration with the provided configuration object.
   * @function
   * @param config - The configuration object containing various settings and parameters.
   * @example
   * const config = { ... };
   * initialiser.initCoreConfig(config);
   * @returns void
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  initCoreConfig = async (config: ConfigurationType): Promise<void> => {
    const {
      routingInfo,
      serviceConfig,
      semanticUrl,
      queryParams,
      noPortal,
      noHistory,
      viewName,
      theme,
      restServerConfig,
      dynamicLoadComponents = true,
      dynamicSemanticUrl = true,
      renderingMode = 'FULL_PORTAL',
      envType,
      extras
    } = config;
    const { appAlias, contextPath } = serviceConfig;

    let restServer = window.location.origin;
    const behaviorOverrides: { dynamicLoadComponents?: boolean; dynamicSemanticUrl?: boolean } = {}; // Used below to set/check Mashup settings that will change bootstrap behavior

    // config.restServerConfig can be specified to override the default rest server
    if (undefined !== restServerConfig) {
      // Mashup (Quasar) usage - use provided override in restServerConfig
      restServer = restServerConfig;
    }

    if (contextPath) {
      restServer = `${restServer}${contextPath}`;
    }
    // instead !restServerConfig(restServerConfig also set in case of webembed), no need to add appAlias in restServer in Launchpad runtime, as dx calls will break.
    if (appAlias && envType !== 'LAUNCHPAD') {
      restServer = `${restServer}/${appAlias}`;
    }
    config.restServerConfig = restServer;
    // Set up behaviorOverrides as specified in config for Mashup (Quasar) usage
    //  Note that, for normal use, dynamicSemanticUrl, dynamicLoadComponents
    //  and dynamicSetCookie default to true

    // Resolve relative <link> URLs to absolute before pushState changes the URL,
    // otherwise the browser may resolve relative hrefs (e.g. favicons) against the new URL.
    this.#resolveHeadFaviconLinkUrls();

    if (dynamicSemanticUrl === true) {
      // Standard usage - no override
      const semUrl = this.buildSemanticUrl(semanticUrl, queryParams, noPortal, restServer, viewName);
      // Do not push history state for bridge cases in infinity
      if (!noHistory && semUrl) {
        window.history.pushState({}, 'home', semUrl);
      }
    } else if (!(renderingMode === 'HYBRID')) {
      // Mashup (Quasar) usage
      let redirectUrl = window.location.href;
      if (redirectUrl.indexOf('?') > 0) {
        redirectUrl = redirectUrl.substring(0, redirectUrl.indexOf('?'));

        window.history.pushState({}, 'home', redirectUrl);
      }
      // make as override, so npm-core/src/router/app-router.js doesn't create semantic url
      behaviorOverrides.dynamicSemanticUrl = false;
    } else {
      console.log(`renderingMode :${renderingMode}`);
    }

    if (dynamicLoadComponents !== true) {
      // Mashup (Quasar) usage
      behaviorOverrides.dynamicLoadComponents = false;
    }

    // remaining use of appAlias is setting up the calls to the c11n service
    // these calls should not be prepended with any form of additional servlet; app/{app-alias} only. bug-627186
    let appAliasNarrow: string | null = null;
    if (appAlias) {
      const idx = appAlias.indexOf('/app/');
      appAliasNarrow = idx >= 0 ? appAlias.substring(idx + 1) : appAlias;
    }

    if (Object.keys(behaviorOverrides).length > 0) {
      // set it here, since it came in as false
      window.PCore.setBehaviorOverrides(behaviorOverrides);
    }

    config.appAlias = appAliasNarrow;

    /* Set Theme overrides to the instance */
    if (theme) {
      let themeObj = typeof theme === 'object' ? theme : null;
      if (themeObj === null) {
        try {
          themeObj = JSON.parse(theme);
        } catch {
          themeObj = {};
        }
      }
      config.theme = themeObj;
    }

    const advRoutingInfo = {
      ...routingInfo,
      domain: `${window.location.protocol}//${window.location.host}`,
      searchParams: window.location.search
    };

    config.routingInfo = advRoutingInfo;
    config.initBroadcast =
      EnvironmentInfo.environmentInfoObject?.pxMashupDetails?.pyIsTraditionalCoexistence === 'true';
    if (config.noPortal && (config.noPortal === 'true' || config.noPortal === true)) {
      config.renderingMode = 'EMBED';
    }
    if (extras?.cspNonce) {
      // cspell:disable-next-line
      window.__webpack_nonce__ = extras.cspNonce;
    }
    await this.init(config);
  };

  /**
   * Launches a view by name.
   * @function
   * @param targetDom - The target DOM element where the view will be rendered.
   * @param viewName - The name of the view to launch.
   * @param portalName - The name of the portal associated with the view.
   * @param viewClass - The class of the view to launch.
   * @param additionalComponents - Additional components to include in the view.
   * @param portalTarget - The target element for the portal.
   * @param styleSheetTarget - The target element for the stylesheet.
   * @param containerTargetName - The name of the container target.
   * @example
   * const targetDom = 'app-root';
   * const viewName = 'HomeView';
   * const portalName = 'MainPortal';
   * const viewClass = 'HomeViewClass';
   * const additionalComponents = { component1: {}, component2: {} };
   * const portalTarget = 'portal-root';
   * const styleSheetTarget = 'style-root';
   * const containerTargetName = 'homeContainer';
   * initialiser.launchViewByName(targetDom, viewName, portalName, viewClass, additionalComponents, portalTarget, styleSheetTarget, containerTargetName);
   * @returns void
   */
  launchViewByName = (
    targetDom: string,
    viewName: string,
    portalName: string,
    viewClass: string,
    additionalComponents: string[],
    portalTarget: string,
    styleSheetTarget: string,
    containerTargetName: string
  ): void => {
    containerCount += 1;
    const containerName = containerTargetName || `mashup${containerCount}`;
    const rootContainer = {
      type: 'RootContainer',
      config: {
        viewConfig,
        renderingMode: 'view',
        name: containerName
      }
    };
    BootstrapUtils.loadRootComponent(
      targetDom,
      rootContainer,
      additionalComponents,
      portalTarget,
      styleSheetTarget
    ).then(() => {
      return BootstrapUtils.loadViewByName(viewName, portalName, viewClass, containerName);
    });
  };

  /**
   * Launches a view into a target DOM element.
   * @function
   * @param targetDom - The target DOM element where the view will be rendered.
   * @param viewMetadata - Metadata for the view being loaded.
   * @param preLoadComponents - (Optional) List of components to preload.
   * @param runtimeParams - (Optional) Runtime parameters for the view.
   * @param portalTarget - (Optional) The target element for the portal.
   * @param styleSheetTarget - (Optional) The target element for the stylesheet.
   * @param containerTargetName - The name of the container target.
   * @example
   * const targetDom = 'app-root';
   * const viewMetadata = { name: 'HomeView', config: { ... } };
   * const preLoadComponents = ['Component1', 'Component2'];
   * const runtimeParams = { param1: 'value1' };
   * const portalTarget = 'portal-root';
   * const styleSheetTarget = 'style-root';
   * const containerTargetName = 'homeContainer';
   * initialiser.launchView(targetDom, viewMetadata, preLoadComponents, runtimeParams, portalTarget, styleSheetTarget, containerTargetName);
   * @returns void
   */
  launchView = (
    targetDom: string,
    viewMetadata: { [key: string]: any },
    // eslint-disable-next-line @typescript-eslint/default-param-last
    preLoadComponents: string[] = [],
    runtimeParams: { [key: string]: any },
    portalTarget: string,
    styleSheetTarget: string,
    containerTargetName: string
  ): void => {
    containerCount += 1;
    const targetName = containerTargetName === 'hybrid' ? `hybrid_${containerCount}` : containerTargetName;
    containerCount += 1;
    const containerName = targetName || `mashup${containerCount}`;
    const rootContainer = {
      type: 'RootContainer',
      config: {
        viewConfig,
        renderingMode: 'portal',
        name: containerName
      }
    };
    if (containerTargetName === Constants.HYBRID) {
      RuntimeParamsAPI.setRuntimeParam(Constants.HYRBID_ROOT_CONTAINERNAME, containerName);
    }
    RuntimeParamsAPI.setRuntimeParams(runtimeParams);
    BootstrapUtils.loadRootComponent(targetDom, rootContainer, preLoadComponents, portalTarget, styleSheetTarget).then(
      () => BootstrapUtils.updateStoreWithUiRoot(viewMetadata, containerName, false)
    );
  };

  /**
   * Launches a portal in constellation.
   * @function
   * @param targetDom - The target DOM element where the portal will be rendered.
   * @param portalName - The name of the portal to launch.
   * @param containerTargetName - The name of the container target.
   * @param preLoadComponents - Additional components to preload.
   * @returns A promise that resolves when the portal is launched.
   * @example
   * const targetDom = 'app-root';
   * const portalName = 'MainPortal';
   * const containerTargetName = 'portalContainer';
   * const preLoadComponents = ['Component1', 'Component2'];
   * initialiser.launchPortal(targetDom, portalName, containerTargetName, preLoadComponents).then(() => console.log('Portal launched'));
   */
  launchPortal = async (
    targetDom: string,
    portalName: string,
    containerTargetName: string = `portal${(containerCount += 1)}`,
    preLoadComponents: string[] = []
  ) => {
    const rootContainer = {
      type: 'RootContainer',
      config: {
        viewConfig,
        renderingMode: 'portal',
        skeleton: 'LoadingComponent',
        name: containerTargetName
      }
    };

    const updatedPreLoadComponents = [...preLoadComponents, rootContainer.config.skeleton];

    EnvironmentInfo.isPortalLoaded = true;

    try {
      await BootstrapUtils.loadRootComponent(targetDom, rootContainer, updatedPreLoadComponents);

      return BootstrapUtils.loadPortalView(portalName, containerTargetName);
    } catch (error) {
      console.error(`Failed to launch portal ${portalName}:`, error);
      throw error;
    }
  };

  /**
   * Launches a mashup/embed for constellation.
   * @function
   * @param targetDom - The target DOM element where the mashup will be rendered.
   * @param usePegaMashupStyling - Whether to apply Pega mashup styling.
   * @example
   * const targetDom = 'app-root';
   * const useStyling = true;
   * initialiser.launchMashup(targetDom, usePegaMashupStyling);
   * @returns void
   */
  launchMashup = (targetDom: string, usePegaMashupStyling = true): void => {
    const rootMashUp = {
      type: 'RootContainer',
      config: {
        renderingMode: 'noPortal'
      },
      children: [
        {
          type: 'ViewContainer',
          config: {
            routingInfoAnnotation,
            name: 'primary'
          }
        }
      ]
    };

    // NOTE: We CANNOT force other consumers (ex: Quasar Mashup) to use this styling
    if (usePegaMashupStyling) {
      const targetDomElement = document.getElementsByTagName(targetDom)[0]?.parentNode;
      if (targetDomElement) {
        const mashupStyle = document.createElement('style');
        mashupStyle.setAttribute('id', 'portal-less-styles');
        mashupStyle.innerHTML =
          'app-root.mashup > .case-view, app-root.mashup > .page-view { min-height: 0px !important; }';
        targetDomElement.appendChild(mashupStyle);
      }
    }

    BootstrapUtils.loadRootComponent(targetDom, rootMashUp, [PCore.getConstants().LOAD_VIEW_STRING, 'ViewContainer']);

    /* US-350587 : Support the resize desktop action in mashup */
    if (window.parent !== window) {
      import(`${window.PCore.getAssetLoader().getStaticServerUrl()}constellation-mashup-bridge.js`).then((mod) => {
        mod.mashup.init({
          resizing: 'stretch'
        });
      });
    }
  };

  /**
   * Initiates the bootstrapping of constellation.
   * @function
   * @param config - The configuration object containing various settings and parameters.
   * @param callback - A callback function to handle the completion of the bootstrapping process.
   * @returns A promise associated with the bootstrapping action.
   * @example
   * const config = { ... };
   * const callback = () => console.log('Bootstrapping complete');
   * initialiser.start(config, callback).then(() => console.log('Start complete'));
   */
  start = async (config: any, callback: () => void): Promise<any> => {
    const { appStaticContentServer } = config.serviceConfig;
    const { ccV2LibId, environmentId, ccLibs, isAlternateDesignSystemEnabled, alternateDesignSystemName } = config;

    await this.initCoreConfig(config);

    const loadReactRoot = async () => {
      await this.importReactRoot();
      const devServerURL = EnvironmentInfo.environmentInfoObject?.pyC11nDXCBDevURL;
      if (devServerURL) {
        await AssetLoader.importCustomComponentMap('');
      }
      if (ccV2LibId && !devServerURL) {
        await AssetLoader.importCustomComponentMap(ccV2LibId);
      }
      if (EnvironmentInfo.environmentInfoObject?.pyInfinityStaticService && ccLibs && ccLibs.length > 0) {
        await Promise.all(ccLibs.map((ccLib: string) => AssetLoader.importCustomComponentMap(ccLib)));
      }

      if (isAlternateDesignSystemEnabled === 'true' && alternateDesignSystemName !== 'other') {
        await this.importDesignSystemComponentMap(appStaticContentServer, environmentId, alternateDesignSystemName);
      }

      if (callback) {
        callback();
      }
      return true;
    };

    const bootStrapingFn = () => {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            const componentsRegistry = BootstrapUtils.getComponentsRegistry();
            const lazyMap = componentsRegistry.getLazyMap();

            if ('ReactRoot' in lazyMap) {
              resolve(true); // Resolve immediately if present in lazyMap
            } else {
              const reactRoot = componentsRegistry.getComponent('ReactRoot');
              if (reactRoot) {
                resolve(await loadReactRoot()); // Resolve if reactRoot exists
              } else {
                PubSubUtils.subscribe('component-map-loaded', async () => {
                  resolve(await loadReactRoot());
                });
              }
            }
          } catch (error) {
            reject(error);
          }
        })();
      });
    };

    return bootStrapingFn();
  };

  /**
   * Registers for debug information and sets the application to preview mode.
   * @function
   * @param channel - The BroadcastChannel used to post debug information.
   * @example
   * const channel = new BroadcastChannel('debugChannel');
   * initialiser.registerForDebugInfo(channel);
   * @returns void
   */
  registerForDebugInfo = (channel: { [key: string]: any }): void => {
    PCore.getEnvironmentInfo().setPreviewMode();
    if (channel) {
      PCore.getPubSubUtils().subscribe(
        'fetchSuccess',
        ({ debugInfo }: { debugInfo: { ['X-Pega-App-Debug-ID']: string } }) => {
          const appDebugID = debugInfo?.['X-Pega-App-Debug-ID'];
          channel.postMessage({ appDebugID });
        },
        'fetchSuccess'
      );
      PCore.getPubSubUtils().subscribe(
        'fetchFailure',
        ({ debugInfo }: { debugInfo: { ['X-Pega-App-Debug-ID']: string } }) => {
          const appDebugID = debugInfo?.['X-Pega-App-Debug-ID'];
          channel.postMessage({ appDebugID });
        },
        'fetchFailure'
      );
    }
  };

  /**
   * Toggles the tracer headers based on messages received through the channel.
   * @function
   * @param channel - The BroadcastChannel used to toggle tracer headers.
   * @example
   * const channel = new BroadcastChannel('tracerChannel');
   * initialiser.toggleTracerHeaders(channel);
   * @returns void
   */
  toggleTracerHeaders = (channel: BroadcastChannel): void => {
    channel.addEventListener('message', async (res?: { data?: { enableTracer: boolean } }) => {
      const enableTracerHeaders = res?.data?.enableTracer;

      if (enableTracerHeaders) {
        PCore.getDebugger().enableTracer();
      } else {
        PCore.getDebugger().disableTracer();
      }
    });
  };

  /**
   * Loads environment information based on the provided configuration.
   * @function loadEnvironmentInfo
   * @param config - Configuration object containing environment information.
   * @param config.theme - Theme information.
   * @param config.theme.definition - Definition of the theme in JSON format.
   * @param config.keyMapping - Key mapping information.
   * @param config.cookieComplianceMethod - Method for cookie compliance.
   * @example
   * const config = {
   *   theme: { definition: '{"Branding": {}, "Theme": {}}' },
   *   keyMapping: { ... },
   *   cookieComplianceMethod: 'strict'
   * };
   * initialiser.loadEnvironmentInfo(config).then(() => console.log('Environment info loaded'));
   * @returns Promise<void>
   */
  loadEnvironmentInfo = async (config: {
    theme: { definition: string };
    keyMapping: any;
    cookieComplianceMethod: CookieComplianceMethod;
  }): Promise<void> => {
    if (config.theme && (!window.pms || window.pms.device.systemName !== 'Android')) {
      try {
        const theme = JSON.parse(config.theme.definition);
        PCore.getEnvironmentInfo().setBrandingInfo(theme.Branding);
        PCore.getEnvironmentInfo().setTheme(theme.Theme);
      } catch {
        PCore.getEnvironmentInfo().setBrandingInfo({});
        PCore.getEnvironmentInfo().setTheme({});
      }
    }
    if (config.keyMapping) {
      PCore.getEnvironmentInfo().setKeyMapping(config.keyMapping);
    }
    PCore.getEnvironmentInfo().setCookieComplianceMethod(config.cookieComplianceMethod ?? 'none');
  };

  /**
   * Initializes routing, action target model, and services.
   * @function
   * @param appRoutingTable - The routing information object containing route definitions.
   * @param actionsTargetModel - The action target model mapping actions to targets.
   * @param servicesObj - The services object containing service configurations.
   * @example
   * const routingInfo = { ... };
   * const actionsModel = { ... };
   * const services = { ... };
   * initialiser.#initRouting(routingInfo, actionsModel, services);
   * @returns void
   */
  #initRouting(
    appRoutingTable: RoutingInfo,
    actionsTargetModel?: { [key: string]: any },
    servicesObj?: ServicesType
  ): void {
    init(appRoutingTable);
    TargetMap.buildActionTargetMap(actionsTargetModel);
    Services.init(servicesObj);
  }

  /**
   * Resolves relative href attributes on favicon <link> elements in document head to absolute URLs.
   * Must be called before history.pushState to prevent the browser from resolving
   * relative favicon URLs against the updated URL.
   * Covers rel="icon" (all modern browsers) and rel="apple-touch-icon" (iOS/macOS Safari).
   */
  #resolveHeadFaviconLinkUrls(): void {
    const links = document.querySelectorAll<HTMLLinkElement>(
      'head link[rel~="icon"], head link[rel~="apple-touch-icon"]'
    );
    // link.href (DOM property) is already resolved to an absolute URL by the browser.
    // Writing it back as the attribute value locks in the absolute URL before pushState changes the base.
    links.forEach((link) => {
      link.setAttribute('href', link.href);
    });
  }
}

export default new Initialiser();
